import React, {useEffect, useState} from 'react';
import Table from 'components/ui/Table';
import Button from 'components/ui/Button';
import Spinner from 'components/ui/Spinner';
import InfoCard from 'components/ui/InfoCard';
import OrderPayments from './Payments';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BookingDetailsCustomerData from './bookingDetails/CustomerData';
import {useGetOrderQuery, useReleaseOrderMutation, useUpdateOrderMutation} from 'services/orders';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {formatDate, formatDateTime} from 'utils/intlDate';
import {useGetLocationsQuery} from 'services/locations';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    SelectChangeEvent,
    Typography
} from '@mui/material';
import Chip from '@mui/material/Chip';
import * as Styles from 'layouts/PrivateLayout.styles';
import TextFieldSkeleton from 'components/ui/Skeletons/TextFieldSkeleton';
import SingleSelect from 'components/ui/Select/SingleSelect';
import  {OptionType} from 'components/ui/AutoComplete';
import TextField from 'components/ui/TextField';
import {useGetClassesQuery} from 'services/classes';
import {useUpdateBookingSlotSingleMutation} from 'services/bookingSlots'
import {useUpdateTruckAssignmentMutation} from "../../../services/truckAssignments";

const Order = () => {
    const {orderId = ''} = useParams();
    const navigate = useNavigate();

    const {data: orderData, isLoading, isFetching} = useGetOrderQuery(orderId);
    const [releaseOrder] = useReleaseOrderMutation();
    const [updateOrder] = useUpdateOrderMutation();
    const [updateBookingSlot] = useUpdateBookingSlotSingleMutation();
    const order: any = orderData?.order;
    const [updateTruckAssignment] = useUpdateTruckAssignmentMutation();

    const createdAt = order ? formatDateTime(new Date(order.created_at)) : '';
    const paymentsData = <OrderPayments order={order}/>;
    const productsList = order?.order_line_items ?? [];

    const {data: locationsData, isLoading: isLoadingLocations} = useGetLocationsQuery({limit: 999});
    const {data: trucksData, isLoading: isLoadingTrucks} = useGetClassesQuery({page: 1, limit: 999});

    const locationsOptions: OptionType[] = locationsData?.locations.map((row) => ({
        label: row.name,
        id: row.id,
    })) || [];

    const trucksOptions: OptionType[] = trucksData?.classes?.map((row) => ({
        label: row.name,
        id: row.id,
    })) || [];

    // Set initial states
    const [location, setLocation] = useState('');
    const [confirmedDeliveryDate, setConfirmedDeliveryDate] = useState('');
    const [requestedDeliveryDate, setRequestedDeliveryDate] = useState('');
    const [truckSelection, setTruckSelection] = useState<any>([]);
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zip, setZip] = useState('');
    const [truckTimes, setTruckTimes] = useState<any>(
        {}
    );
    useEffect(() => {
        if (order?.trucks) {
            const initialTruckTimes: {
                [assignmentId: string]: { id: string; assignment_id: string; calculated_time: number }
            } = {};
            order.trucks.forEach((truck: any) => {
                initialTruckTimes[truck.assignment_id] = {
                    id: truck.id,
                    assignment_id: truck.assignment_id,
                    calculated_time: truck.calculated_time
                };
            });
            setTruckTimes(initialTruckTimes);
        }
    }, [order]);


    // Update truck times in the state
    const handleTruckTimeChange = (assignmentId: string, newValue: number) => {
        setTruckTimes((prev: any) => ({
            ...prev,
            [assignmentId]: {
                ...prev[assignmentId],
                calculated_time: newValue // Update only the calculated_time
            }
        }));
    };

    const getCorrectLocationName = (locationId: any) => {
        const locationData: any =  locationsData?.locations.filter((item)=> item.id === locationId);
        if(locationData.length > 0) {
            return locationData[0].name;
        }else{
            return "Location Name not found"
        }
    }

    const getFilteredTruckAvailabilityAtLocation = () => {
        const selectedLocationId = location;

        // Check if trucksData exists
        if (!trucksData) {
            return null;
        }

        // Filter trucks based on location ID
        const filteredTrucksByLocation: any = trucksData?.classes?.filter((truckClass: any) =>
            truckClass.locations.some((loc: any) => loc.id === selectedLocationId)
        );

        // Convert the filtered trucks to a string of names
        if (filteredTrucksByLocation.length === 0) {
            return "No trucks available for the selected location.";
        }

        // Create a comma-separated list of truck names
        const truckNames = filteredTrucksByLocation.map((truck: any) => truck.name).join(", ");

        // Return the formatted string
        return `Available trucks: ${truckNames}`;
    };


    // Save the updated truck time using `assignment_id`
    const handleSaveTruckTime = async (assignmentId: string) => {
        const truckData = truckTimes[assignmentId]; // Get full object
        try {
            await updateTruckAssignment({
                id: truckData.assignment_id,
                body: {calculated_time: truckData.calculated_time},
            }).unwrap();
            window.location.reload()
        } catch (error) {
            console.error('Error updating truck assignment:', error);
            alert('Failed to update truck assignment.');
        }
    };


    // State for confirmation dialo g visibility
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

    // Update states after orderData, locationsData, and trucksData are loaded
    useEffect(() => {
        if (order && locationsData && trucksData) {
            setLocation(order?.location_id || '');
            setConfirmedDeliveryDate(order?.confirmed_delivery_date ? order?.confirmed_delivery_date.split("T")[0] : '');
            setRequestedDeliveryDate(order?.deliveryDate)
            setTruckSelection(
                order?.trucks?.length > 0
                    ? order.trucks[0].id
                    : null
            );
            setAddress(order?.shippingAddress?.address1 || '');
            setCity(order?.shippingAddress?.city || '');
            setState(order?.shippingAddress?.province || '');
            setZip(order?.shippingAddress?.zip || '');
        }
    }, [order, locationsData, trucksData]);

    // Handlers for form changes
    const handleLocationChange = (event: SelectChangeEvent<any>) => {
        setLocation(event.target.value);
    };

    const handleTrucksChange = (event: SelectChangeEvent<any>) => {
        setTruckSelection(event.target.value);
    };

    const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setConfirmedDeliveryDate(event.target.value);
    };

    const handleRequestDeliveryDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRequestedDeliveryDate(event.target.value);
    };

    // Open confirmation dialog
    const openConfirmDialog = () => setIsConfirmDialogOpen(true);

    // Close confirmation dialog
    const closeConfirmDialog = () => setIsConfirmDialogOpen(false);

    // Handle confirmation action
    const handleConfirmRelease = async () => {
        // Validate that a confirmed delivery date is provided
        if (!confirmedDeliveryDate) {
            // Close the confirmation dialog
            closeConfirmDialog();
            alert("Please select a confirmed delivery date before releasing the order.");
            return; // Stop execution if the date is not provided
        }

        // Close the confirmation dialog
        closeConfirmDialog();

        // Prepare the payload for updating the order
        const payload = {
            location_id: location,
            confirmed_delivery_date: confirmedDeliveryDate,
            trucks: truckSelection.map((truck: any) => ({ id: truck.id })),
            address: {
                address1: address,
                city,
                state,
                zip,
            },
        };

        try {
            await releaseOrder({ orderId, body: payload }).unwrap();
            alert("Order successfully released.");
        } catch (error) {
            console.error("Error releasing order:", error);
            alert("Failed to release order.");
        }
    };

    const handleChangeLocation = async () => {
        if (!location) {
            closeConfirmDialog();
            alert("Please select a location before attempting to save it.");
            return;
        }

        // Validate that the selected truck belongs to the selected location
        const selectedLocationId = location;
        if (truckSelection) {
            const truck = trucksData?.classes?.find((truckClass: any) => {
                return truckClass.id === truckSelection
            });
            const isTruckValid = truck?.locations?.some((loc: any) => loc.id === selectedLocationId);
            if (!isTruckValid) {
                alert(`The selected truck does not belong to the selected location.`);
                return;
            }
        }

        const confirmSave = window.confirm(
            "Are you sure you want to save the new location and truck assignment?\n\n" +
            "**WARNING:** Changing the location or truck assignment may affect scheduling and delivery timelines."
        );

        if (!confirmSave) {
            return;
        }

        closeConfirmDialog();



        try {
            const payload = {
                location_id: location,
            };

            const bookingSlotChange = {
                location_id: location,
                class_id: truckSelection
            };

            const changeTruckAssignment = {
                class_id: truckSelection,
                date_start: requestedDeliveryDate,
                location_id: location,
            }

            await updateTruckAssignment({
                id: order.trucks[0].assignment_id,
                body: changeTruckAssignment
            }).unwrap();

            const bookingSlotId = order.bookingSlot.id;

            // Update booking slot with new location
            await updateBookingSlot({ bookingId: bookingSlotId, body: bookingSlotChange }).unwrap();

            // Update order with new location
            await updateOrder({ orderId, body: payload }).unwrap();

            alert("Order Location updated successfully.");
            window.location.reload()
        } catch (error) {
            console.error("Error updating location ", error);
            alert("Error updating location ");
        }
    };

    const handleChangeRequestedDeliveryDate = async () => {
        if (!requestedDeliveryDate) {
            closeConfirmDialog();
            alert("There was an error on the requested delivery date.");
            return;
        }

        const confirmSave = window.confirm(
            "Are you sure you want to change the Request Delivery Date?\n\n" +
            "**WARNING - Overrides to the Requested Date will impact the remaining time allowed to schedule a delivery on that date."
        );

        if (!confirmSave) {
            return;
        }

        closeConfirmDialog();
        try {
            const orderDeliveryDate = {
                delivery_date: requestedDeliveryDate,
            };

            const bookingSlotChange = {
                date_start: requestedDeliveryDate,
                date_end: requestedDeliveryDate,
            }

            const bookingSlotId = order.bookingSlot.id;
            await updateBookingSlot({ bookingId: bookingSlotId , body: bookingSlotChange}).unwrap();
            await updateOrder({ orderId, body: orderDeliveryDate }).unwrap();
            alert("Requested delivery date changed");
            window.location.reload()
        } catch (error) {
            console.error("Error changing requested delivery date ", error);
            alert("Error changing requested delivery date");
        }
    };

    const lineItemsColumns = [
        {
            field: 'name',
            headerName: 'Product',
            render: (data: any) => `${data.name}`
        },
        {
            field: 'created_at',
            headerName: 'Purchase Date',
            render: (data: any) => formatDate(new Date(data.created_at))
        },
        {
            field: 'quantity',
            headerName: 'Quantity',
            render: (data: any) => data.quantity
        }
    ];

    const customerData = <BookingDetailsCustomerData booking={order}/>;

    // Function to open Shopify Order Printer app with selected order ID(s)
    const handleOrderPrinterRedirect = () => {
        const basePrinterUrl = `${process.env.REACT_APP_SHOPIFY_URL}/apps/order-printer-emailer/print/orders`;

        // Prepare the URL with the order ID in the query string format
        const printerUrl = `${basePrinterUrl}?id=${order?.source_order_id}`;

        // Open the Order Printer app URL in a new tab
        window.open(printerUrl, '_blank', 'noopener,noreferrer');
    };


    const getCalculatedDeliveryTime = () => {
        let results = 0;
        if (orderData) {
            orderData.order.trucks.forEach((item: any) => results += item.calculated_time);
            return `${results} Minutes`;
        }
    };

    const isDelivered = () => {
        if(order.confirmed_delivery_date) {
            return true
        }else{
            return false
        }
    }

    const getDeliveryStatusBadge = () => {
        if (isDelivered()) {
            return (
                <Chip
                    label="Confirmed"
                    color="success"
                    sx={{
                        backgroundColor: 'green',
                        color: 'white',
                        fontWeight: 'bold',
                    }}
                />
            );
        } else {
            return (
                <Chip
                    label="Pending"
                    color="primary"
                    sx={{
                        backgroundColor: 'blue',
                        color: 'white',
                        fontWeight: 'bold',
                    }}
                />
            );
        }
    };

    const getViewDeliveryDate = () => {
        const deliveryDate = order.confirmed_delivery_date
            ? order.confirmed_delivery_date
            : order.deliveryDate;

        return `/admin/locations/${order?.location_id}/${deliveryDate}`;
    };
    return (
        <>
            {!isFetching && !isLoading ? (
                <div className="flex flex-col gap-6">
                    <Styles.FlexContainer className="pb-3 border-b">
                        <Styles.FlexContainerColumn>
                            <ArrowBackIcon
                                sx={{ fontSize: 24, cursor: "pointer" }}
                                onClick={() => navigate(-1)}
                            />
                        </Styles.FlexContainerColumn>
                        <Styles.FlexContainerColumn className="pl-4">
                            <Typography sx={{fontSize: 18}} className="ml-4">
                                #{order?.order_number}
                            </Typography>
                            <Typography
                                sx={{fontSize: 14}}
                                color="text.secondary"
                                gutterBottom
                            >
                                Created: {createdAt}
                            </Typography>
                        </Styles.FlexContainerColumn>
                        <Styles.FlexContainerColumn push-right-lg>
                            <Styles.PageTitleContainerButtons>
                                <Button onClick={handleOrderPrinterRedirect}>Print</Button>
                                <Button
                                    onClick={() => {
                                        navigate(getViewDeliveryDate());
                                    }}
                                >
                                    View delivery day
                                </Button>
                                <Button cc-color="purple" onClick={openConfirmDialog} disabled={order?.confirmed_delivery_date}>
                                    Release Delivery
                                </Button>
                            </Styles.PageTitleContainerButtons>
                        </Styles.FlexContainerColumn>
                    </Styles.FlexContainer>
                    <div className="grid grid-cols-1 xl:grid-cols-[30%_auto_10%] gap-6 xl:gap-16">
                        <InfoCard label="Customer" isLoading={false} value={customerData}/>
                        <div className="flex flex-col gap-10">
                            <div>
                                <TextFieldSkeleton isLoading={isLoadingLocations}>
                                    <SingleSelect
                                        id="locationId"
                                        label="Select Location"
                                        value={location}
                                        onChange={handleLocationChange}
                                        required
                                        options={[{id: '', label: 'Select Location'}, ...locationsOptions]}
                                    />
                                </TextFieldSkeleton>
                            </div>
                            <div>
                                {isLoadingTrucks ? (
                                    <Spinner/>
                                ) : (
                                    <>
                                        <div style={{fontSize: 12, paddingTop: 2}}>
                                            {`At location ${getCorrectLocationName(location)} these trucks are available:`}
                                        </div>
                                        <div style={{fontSize: 12, paddingTop: 2, marginBottom: 30}}>
                                            {`${getFilteredTruckAvailabilityAtLocation()}`}
                                        </div>
                                        <SingleSelect
                                            id="trucks"
                                            label="Truck"
                                            value={truckSelection}
                                            onChange={handleTrucksChange}
                                            required
                                            options={trucksOptions}
                                        />
                                        <div style={{fontSize: 12, paddingTop: 2}}>**WARNING -- If you change the
                                            location, you need to ensure that the truck selected below belongs in that
                                            location
                                        </div>
                                        <Button
                                            sx={{marginTop: 1}}
                                            key={'edit'}
                                            onClick={handleChangeLocation}
                                            cc-color={"purple"}
                                            disabled={order?.confirmed_delivery_date}
                                        >
                                            Save new Location assignment & Truck assignment
                                        </Button>
                                    </>
                                )}
                            </div>

                            <div>
                                <label className="order-view-label" htmlFor="confirmedDeliveryDate">Confirmed Delivery
                                    Date</label>
                                <TextField
                                    id="confirmedDeliveryDate"
                                    type="date"
                                    value={confirmedDeliveryDate}
                                    onChange={handleDateChange}
                                    cc-width="full"
                                />
                                <div style={{marginTop: 30}}/>
                                <label className="order-view-label" htmlFor="confirmedDeliveryDate">Requested Delivery
                                    Date</label>
                                <TextField
                                    id="requestedDeliveryDate"
                                    type="date"
                                    value={requestedDeliveryDate}
                                    onChange={handleRequestDeliveryDateChange}
                                    cc-width="full"
                                />
                                <div style={{fontSize: 12, paddingTop: 2, marginBottom: 14}}>**WARNING - Overrides to the Requested Date will impact the remaining time allowed to schedule a delivery on that date
                                </div>
                                <Button
                                    key={'edit'}
                                    onClick={handleChangeRequestedDeliveryDate}
                                    sx={{marginTop: 1}}
                                    cc-color={"purple"}
                                    disabled={order?.confirmed_delivery_date}
                                >
                                    Save Requested Delivery Date
                                </Button>
                            </div>
                            <div>
                                <h3 style={{paddingBottom: 10}}>Truck Time Assignment Overrides</h3>
                                <h4 style={{fontSize: 12, maxWidth: 500}}>** WARNING - Overrides to the original time any of the
                                    following trucks
                                    will impact the remaining
                                    time allowed to schedule a
                                    delivery</h4>
                                {order?.trucks.map((truck: any) => (
                                    <div
                                        key={truck.id}
                                        className="flex items-center justify-between mb-4"
                                    >
                                        <div>
                                            <span>Truck: {truck.name} ( Current Alloted Time: {truck.calculated_time} min )</span>
                                        </div>
                                        <div>
                                            <TextField
                                                id={`truckTime-${truck.assignment_id}`}
                                                label="Allotted Time"
                                                value={truckTimes[truck.assignment_id]?.calculated_time || 0} // Access calculated_time
                                                onChange={(e) =>
                                                    handleTruckTimeChange(truck.assignment_id, Number(e.target.value))
                                                }
                                            />
                                            <Button
                                                onClick={() => handleSaveTruckTime(truck.assignment_id)}
                                                cc-color="purple"
                                                style={{marginLeft: "10px"}}
                                            >
                                                Save
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div>
                                <label className="order-view-label" style={{marginBottom: 19}} htmlFor="address">Delivery
                                    Address</label>
                                <div className="flex flex-col gap-5">
                                    <TextField id="address" label="Address" required cc-width="full" value={address}
                                               onChange={(e) => setAddress(e.target.value)}/>
                                    <Styles.SectionThreeColumns className="!gap-2">
                                        <TextField id="city" label="City" required cc-width="full" value={city}
                                                   onChange={(e) => setCity(e.target.value)}/>
                                        <TextField id="state" label="State" required cc-width="full" value={state}
                                                   onChange={(e) => setState(e.target.value)}/>
                                        <TextField id="zip" label="ZIP" required cc-width="full" value={zip}
                                                   onChange={(e) => setZip(e.target.value)}/>
                                    </Styles.SectionThreeColumns>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col text-right">
                            <label className="order-view-label justify-end">Delivery Time</label>
                            <span style={{marginBottom: 10}}>{getCalculatedDeliveryTime()}</span>

                            <label className="order-view-label justify-end">Status</label>
                            {getDeliveryStatusBadge()}
                        </div>
                    </div>
                    <Table rows={productsList} columns={lineItemsColumns} usePointer/>
                    <div className="py-2"></div>
                    <InfoCard label="PAID" isLoading={false} value={paymentsData}/>
                </div>
            ) : (
                <Spinner/>
            )}
            <Dialog open={isConfirmDialogOpen} onClose={closeConfirmDialog}>
                <DialogTitle>Confirm Release</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to release this order? This action will confirm the delivery date,
                        location, and assigned trucks.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeConfirmDialog} cc-color='red'>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmRelease} cc-color='purple'>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default Order;
